import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ConfirmModal } from '@/components/ui/Modal';
import { PlanForm } from '@/components/forms/PlanForm';
import { usePlan, useUpdatePlan, useDeletePlan } from '@/hooks/usePlans';

export const PlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const { data: plan, isLoading } = usePlan(id!);
  const updateMutation = useUpdatePlan();
  const deleteMutation = useDeletePlan();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Plano nao encontrado</p>
        <Button variant="ghost" onClick={() => navigate('/admin/plans')} className="mt-4">
          Voltar
        </Button>
      </div>
    );
  }

  const handleSubmit = async (data: Parameters<typeof updateMutation.mutate>[0]['dto']) => {
    await updateMutation.mutateAsync({ id: plan.id, dto: data });
    navigate('/admin/plans');
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(plan.id);
    navigate('/admin/plans');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/plans')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Editar Plano: {plan.name}
          </h1>
        </div>
        <Button
          variant="danger"
          leftIcon={<Trash2 className="w-4 h-4" />}
          onClick={() => setShowDeleteModal(true)}
        >
          Excluir
        </Button>
      </div>

      {/* Form */}
      <PlanForm
        defaultValues={{
          name: plan.name,
          slug: plan.slug,
          type: plan.type,
          currency: plan.currency,
          priceMonthly: plan.priceMonthly,
          priceYearly: plan.priceYearly,
          description: plan.description,
          features: plan.features,
          isActive: plan.isActive,
          displayOrder: plan.displayOrder,
          stripePriceIdMonthly: plan.stripePriceIdMonthly,
          stripePriceIdYearly: plan.stripePriceIdYearly,
          limits: plan.limits,
        }}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Excluir Plano"
        message={`Tem certeza que deseja excluir o plano "${plan.name}"? Esta acao nao pode ser desfeita.`}
        confirmText="Excluir"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default PlanDetailPage;
